import { useEffect, useRef, useState } from 'react';
import { MessageCircle, X, Send, Loader2 } from 'lucide-react';
import './ChatWidget.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SESSION_KEY = 'webdream_chat_session';
const HISTORY_KEY = 'webdream_chat_history';

const genSessionId = () => {
  if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
  return 'sess-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
};

const getSessionId = () => {
  let sid = localStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid = genSessionId();
    localStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
};

const genId = () => {
  if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
  return 'msg-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
};

const INTRO_MESSAGE = {
  id: 'intro',
  role: 'assistant',
  content: "Bonjour ! Je suis l'assistant Webdream.fr. Je peux vous renseigner sur nos offres (Vitrine 500€ ou Site Web 1000€) ou cadrer votre projet avec vous. Que puis-je faire pour vous ?"
};

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [unread, setUnread] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const sessionIdRef = useRef(null);

  // Init session + load cached history
  useEffect(() => {
    sessionIdRef.current = getSessionId();
    try {
      const cached = JSON.parse(localStorage.getItem(HISTORY_KEY) || 'null');
      if (Array.isArray(cached) && cached.length) {
        // Backwards-compat: ensure every cached message has a stable id
        setMessages(cached.map((m) => (m.id ? m : { ...m, id: genId() })));
      } else {
        setMessages([INTRO_MESSAGE]);
      }
    } catch (err) {
      console.warn('ChatWidget: unable to restore history', err);
      setMessages([INTRO_MESSAGE]);
    }
  }, []);

  // Persist history
  useEffect(() => {
    if (messages.length) {
      try {
        localStorage.setItem(HISTORY_KEY, JSON.stringify(messages));
      } catch (err) {
        console.warn('ChatWidget: unable to persist history', err);
      }
    }
  }, [messages]);

  // Autoscroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  // Focus input on open
  useEffect(() => {
    if (open) {
      setUnread(false);
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [open]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    const userMsg = { id: genId(), role: 'user', content: text };
    const placeholderId = genId();
    setMessages((m) => [...m, userMsg, { id: placeholderId, role: 'assistant', content: '', streaming: true }]);
    setSending(true);

    try {
      const res = await fetch(`${BACKEND_URL}/api/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionIdRef.current, message: text })
      });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let accumulated = '';

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // Parse SSE frames
        const parts = buffer.split('\n\n');
        buffer = parts.pop() || '';
        for (const frame of parts) {
          const line = frame.split('\n').find((l) => l.startsWith('data:'));
          if (!line) continue;
          const payload = line.slice(5).trim();
          if (!payload) continue;
          try {
            const data = JSON.parse(payload);
            if (data.delta) {
              accumulated += data.delta;
              setMessages((m) => {
                const copy = [...m];
                const last = copy[copy.length - 1];
                if (last && last.role === 'assistant' && last.streaming) {
                  copy[copy.length - 1] = { ...last, content: accumulated };
                }
                return copy;
              });
            } else if (data.error) {
              throw new Error(data.error);
            }
          } catch (parseErr) {
            // Partial SSE frame — the remainder stays in buffer for the next iteration
            if (parseErr && parseErr.message && !parseErr.message.startsWith('Unexpected')) {
              console.warn('ChatWidget: SSE payload issue', parseErr);
            }
          }
        }
      }

      setMessages((m) => {
        const copy = [...m];
        const last = copy[copy.length - 1];
        if (last && last.role === 'assistant' && last.streaming) {
          copy[copy.length - 1] = { id: last.id, role: 'assistant', content: accumulated || 'Désolé, aucune réponse reçue.' };
        }
        return copy;
      });
    } catch (err) {
      console.warn('ChatWidget: stream error', err);
      setMessages((m) => {
        const copy = [...m];
        const last = copy[copy.length - 1];
        if (last && last.role === 'assistant' && last.streaming) {
          copy[copy.length - 1] = {
            id: last.id,
            role: 'assistant',
            content: "Oups, une erreur est survenue. Réessayez ou contactez directement Nolan au 06 69 47 75 36."
          };
        }
        return copy;
      });
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const resetConversation = () => {
    localStorage.removeItem(HISTORY_KEY);
    localStorage.removeItem(SESSION_KEY);
    sessionIdRef.current = getSessionId();
    setMessages([INTRO_MESSAGE]);
  };

  return (
    <>
      <button
        type="button"
        className={`chat-fab${unread ? ' has-unread' : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? 'Fermer le chat' : 'Ouvrir le chat'}
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
        {!open && <span className="chat-fab-label">Assistant IA</span>}
      </button>

      <div className={`chat-panel${open ? ' open' : ''}`} role="dialog" aria-hidden={!open}>
        <div className="chat-header">
          <div className="chat-header-info">
            <div className="chat-avatar">
              <span className="chat-avatar-dot"></span>
            </div>
            <div>
              <div className="chat-title">Assistant Webdream.fr</div>
              <div className="chat-status">En ligne · réponse immédiate</div>
            </div>
          </div>
          <button type="button" className="chat-reset" onClick={resetConversation} title="Nouvelle conversation">
            Réinitialiser
          </button>
        </div>

        <div className="chat-messages" ref={scrollRef}>
          {messages.map((m) => (
            <div key={m.id} className={`chat-bubble ${m.role}`}>
              {m.content || (m.streaming ? <Loader2 size={14} className="chat-loader" /> : null)}
            </div>
          ))}
        </div>

        <div className="chat-input-wrap">
          <textarea
            ref={inputRef}
            className="chat-input"
            placeholder="Posez votre question..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={1}
            disabled={sending}
          />
          <button
            type="button"
            className="chat-send"
            onClick={sendMessage}
            disabled={sending || !input.trim()}
            aria-label="Envoyer"
          >
            {sending ? <Loader2 size={18} className="chat-loader" /> : <Send size={18} />}
          </button>
        </div>
      </div>
    </>
  );
};

export default ChatWidget;
